/**
 * Field construction helpers for the section projectors.
 *
 * WHY THIS EXISTS
 *
 * Every projector faces the same three chores, and getting any of them wrong is
 * invisible in the output:
 *
 *  1. Deciding whether a value is actually present. `''`, `'   '`, `undefined`,
 *     `null`, and `[]` all mean "nothing to show", and each projector reinventing
 *     that check is how a stray empty string ends up rendered as a blank row.
 *  2. Distinguishing NOT PROVIDED from NOT APPLICABLE. A blank dual-citizenship
 *     country on a non-dual-citizen is not missing data; labelling it "Not
 *     provided" invents an omission. Conditional sections are full of this, so
 *     the absence reason is an explicit argument, never a default guess.
 *  3. Resolving a coded value through its label map without crashing on a value
 *     the map has never heard of — legacy rows do carry retired codes.
 *
 * These builders are the single place each of those decisions is made.
 */

import type { YesNo } from '../../oasis-application.types';
import { YES_NO_LABELS } from '../oasis-display-labels';
import type { DisplayAbsenceReason, DisplayField, DisplayFileRef, DisplayValue } from '../oasis-display.types';

/** True when a scalar carries no information a reader would want. */
export function isBlank(value: unknown): boolean {
	if (value === null || value === undefined) return true;
	if (typeof value === 'string') return value.trim() === '';
	if (Array.isArray(value)) return value.length === 0;
	return false;
}

/** An absent value, tagged with WHY it is absent. */
export function empty(reason: DisplayAbsenceReason = 'not_provided'): DisplayValue {
	return { kind: 'empty', reason };
}

export function field(label: string, value: DisplayValue, hint?: string): DisplayField {
	return hint ? { label, value, hint } : { label, value };
}

/** A short single-line answer. Blank input becomes an explicit absence. */
export function textField(label: string, value: string | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	return field(label, isBlank(value) ? empty(reason) : { kind: 'text', value: (value as string).trim() });
}

/** Free text that may contain paragraphs. Whitespace inside is preserved. */
export function longTextField(label: string, value: string | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	return field(label, isBlank(value) ? empty(reason) : { kind: 'longText', value: value as string });
}

/**
 * A date-only value, carried raw. See `formatIsoDate` for why it is not
 * formatted here.
 */
export function dateField(label: string, iso: string | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	return field(label, isBlank(iso) ? empty(reason) : { kind: 'date', iso: (iso as string).trim() });
}

export function dateRangeField(label: string, fromIso: string | null | undefined, toIso: string | null | undefined, ongoing = false): DisplayField {
	if (isBlank(fromIso) && isBlank(toIso) && !ongoing) return field(label, empty());
	return field(label, { kind: 'dateRange', fromIso: fromIso?.trim() ?? '', toIso: toIso?.trim() ?? '', ongoing });
}

export function listField(label: string, items: readonly string[] | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	const cleaned = (items ?? []).map((i) => i?.trim()).filter((i): i is string => !!i);
	return field(label, cleaned.length === 0 ? empty(reason) : { kind: 'list', items: cleaned });
}

export function linkField(label: string, href: string | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	if (isBlank(href)) return field(label, empty(reason));
	const url = (href as string).trim();
	return field(label, { kind: 'link', href: url, text: url });
}

export function yesNoField(label: string, value: YesNo | '' | null | undefined, reason: DisplayAbsenceReason = 'not_provided'): DisplayField {
	if (value !== 'yes' && value !== 'no') return field(label, empty(reason));
	return field(label, { kind: 'text', value: YES_NO_LABELS[value] });
}

/**
 * Resolves a coded value through its label map.
 *
 * A code with no entry in the map falls back to the raw code rather than an
 * empty row. That is deliberate: a retired enum member on a stored row is a
 * data-migration problem, and showing `some_retired_code` makes it findable,
 * whereas silently dropping the field hides that the applicant answered at all.
 */
export function enumField<T extends string>(
	label: string,
	value: T | '' | null | undefined,
	labels: Record<T, string>,
	reason: DisplayAbsenceReason = 'not_provided',
): DisplayField {
	if (isBlank(value)) return field(label, empty(reason));
	const code = value as T;
	return field(label, { kind: 'text', value: labels[code] ?? code });
}

/** Resolves several coded values, dropping blanks. */
export function enumListField<T extends string>(
	label: string,
	values: readonly T[] | null | undefined,
	labels: Record<T, string>,
	reason: DisplayAbsenceReason = 'not_provided',
): DisplayField {
	const items = (values ?? []).filter((v) => !isBlank(v)).map((v) => labels[v] ?? v);
	return listField(label, items, reason);
}

/** A duration already derived and persisted upstream; never recomputed here. */
export function monthsField(label: string, months: number | null | undefined): DisplayField {
	if (months === null || months === undefined || !Number.isFinite(months) || months <= 0) return field(label, empty());
	return field(label, { kind: 'text', value: String(months) });
}

/** Drops fields whose value is absent, for blocks that should not show gaps. */
export function definedOnly(fields: readonly DisplayField[]): DisplayField[] {
	return fields.filter((f) => f.value.kind !== 'empty');
}

/** Maps a stored upload reference onto its display shape. */
export function fileRef(
	doc: { s3Key: string; filename: string; sizeBytes: number; uploadedAt: string; description?: string },
	docTypeLabel: string,
): DisplayFileRef {
	return {
		s3Key: doc.s3Key,
		filename: doc.filename,
		docTypeLabel,
		sizeBytes: doc.sizeBytes,
		uploadedAtIso: doc.uploadedAt,
		...(doc.description ? { description: doc.description } : {}),
	};
}
